import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import { subscribeToPerformanceRecords, unsubscribeFromPerformanceRecords } from '../../services/performanceService';
import { useAuth } from '../../contexts/AuthContext';

import PlayerPerformanceForm from './components/PlayerPerformanceForm';
import CoachPerformanceForm from './components/CoachPerformanceForm';
import GoalkeeperPerformanceForm from './components/GoalkeeperPerformanceForm';

const PerformanceRecordsSystem = () => {
  const { hasRole } = useAuth();
  const isAdmin = hasRole('admin');
  const [activeRole, setActiveRole] = useState('player');
  const [submitSuccess, setSubmitSuccess] = useState(false);

  // Real-time synchronization
  useEffect(() => {
    const channel = subscribeToPerformanceRecords((table, payload) => {
      console.log(`Real-time update from ${table}:`, payload);
      // You can add additional logic here to update UI based on real-time changes
      // For example, show a notification when a new record is added
    });

    return () => {
      unsubscribeFromPerformanceRecords(channel);
    };
  }, []);

  const roles = [
    { id: 'player', label: 'Player Records', icon: 'User', description: 'Field player performance profiles' },
    { id: 'coach', label: 'Coach Records', icon: 'Clipboard', description: 'Technical staff & coaching profiles' },
    { id: 'goalkeeper', label: 'Goalkeeper Records', icon: 'Shield', description: 'Specialized goalkeeper profiles' }
  ];

  const handleFormSubmit = () => {
    if (!isAdmin) return;
    setSubmitSuccess(true);
    setTimeout(() => setSubmitSuccess(false), 5000);
  };

  return (
    <>
      <Helmet>
        <title>Performance Records System | ProX11 Market</title>
        <meta name="description" content="Comprehensive performance records management for players, coaches, and goalkeepers with detailed career documentation and statistics" />
      </Helmet>

      <div className="min-h-screen bg-background">
        <Header />

        {/* Hero Section */}
        <div className="bg-gradient-to-br from-primary/10 via-background to-secondary/10 border-b border-border">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
            <div className="text-center space-y-4">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 rounded-full">
                <Icon name="Database" size={20} className="text-primary" />
                <span className="text-sm font-medium text-primary">Performance Database</span>
              </div>
              <h1 className="text-4xl md:text-5xl font-bold text-foreground">
                Performance Records System
              </h1>
              <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
                Comprehensive career documentation and performance tracking for players, coaches, and goalkeepers
              </p>
              {!isAdmin && (
                <div className="mt-4 p-4 bg-warning/10 border border-warning/20 rounded-lg max-w-2xl mx-auto">
                  <p className="text-warning font-medium">
                    <Icon name="Lock" size={16} className="inline mr-2" />
                    Admin Access Only: Only administrators can add or edit performance records.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Role Selection Tabs */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
            {roles?.map((role) => (
              <button
                key={role?.id}
                onClick={() => setActiveRole(role?.id)}
                disabled={!isAdmin}
                className={`p-6 rounded-lg border-2 transition-all duration-200 text-left ${
                  activeRole === role?.id
                    ? 'border-primary bg-primary/5 shadow-lg'
                    : 'border-border bg-card hover:border-primary/50 hover:bg-card/80'
                } ${!isAdmin ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                <div className="flex items-start gap-4">
                  <div className={`p-3 rounded-lg ${
                    activeRole === role?.id ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'
                  }`}>
                    <Icon name={role?.icon} size={24} />
                  </div>
                  <div className="flex-1">
                    <h3 className={`text-lg font-semibold mb-1 ${
                      activeRole === role?.id ? 'text-primary' : 'text-foreground'
                    }`}>
                      {role?.label}
                    </h3>
                    <p className="text-sm text-muted-foreground">{role?.description}</p>
                  </div>
                  {activeRole === role?.id && (
                    <Icon name="CheckCircle2" size={20} className="text-primary" />
                  )}
                </div>
              </button>
            ))}
          </div>

          {/* Success Message */}
          {submitSuccess && (
            <div className="mb-6 p-4 bg-success/10 border border-success rounded-lg flex items-center gap-3">
              <Icon name="CheckCircle2" size={20} className="text-success" />
              <p className="text-success font-medium">Performance record submitted successfully!</p>
            </div>
          )}

          {/* Form Content */}
          <div className="bg-card border border-border rounded-lg shadow-lg">
            {activeRole === 'player' && <PlayerPerformanceForm onSubmit={handleFormSubmit} />}
            {activeRole === 'coach' && <CoachPerformanceForm onSubmit={handleFormSubmit} />}
            {activeRole === 'goalkeeper' && <GoalkeeperPerformanceForm onSubmit={handleFormSubmit} />}
          </div>
        </div>
      </div>
    </>
  );
};

export default PerformanceRecordsSystem;